import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import ErrorBoundary from '@/utils/ErrorBoundray';
import { GanttChart, NarrativeGraph, TreeView } from '@/ux-common/src';
import { iconMap } from '@/ux-common/src/lib/helper';
import { DataModel, SpanDetails } from '@/ux-common/src/types/tree-view-types';
import { NarrativeGraphData, NarrativeTurnsData } from 'types/narrative-graph';

import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Icons } from '@/components/icons';
import BasicLoader from '@/components/spinner/basic-loader';

interface ResizablePanelsProps {
  rightPanel: React.ReactNode;
  isRightPanelOpen: boolean;
  readOnly: boolean | undefined;
  viewType: 'treeView' | 'ganttView' | 'narrativeView';
  setViewType: React.Dispatch<
    React.SetStateAction<'treeView' | 'ganttView' | 'narrativeView'>
  >;
  onClick: (id: string) => void;
  treeViewData: DataModel[];
  data: DataModel[];
  setExceptionSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  selectedSpanId: string | null;
  narrativeTurnsData?: NarrativeTurnsData | null;
  narrativeTurnsLoading?: boolean;
  narrativeTurnsError?: Error | null;
  narrativeTurnsFetchingMore?: boolean;
  fetchSingleTrace: (traceId: string) => Promise<any>;
  allTracesForFactId: DataModel[];
  factName?: string;
  loadingTraceId: string | null;
  setLoadingTraceId: React.Dispatch<React.SetStateAction<string | null>>;
  errorTraceId: string | null;
  errorTraceMessage: string | null;
  factId: string | null;
  turnChildrenData?: Record<string, NarrativeGraphData>;
  turnChildrenLoading?: Record<string, boolean>;
  spanDetails?: SpanDetails;
  onExpandTurn?: (turnNodeId: string, traceId: string) => void;
  shouldShowNarrativeView?: boolean;
  tokenCount?: string;
  tokenTooltip?: string;
  enableTracePagination?: boolean;
  tracePageSize?: number;
  tracesTotalCount?: number | null;
  tracesNextPageToken?: string | null;
  tracesLoadingMore?: boolean;
  onLoadMoreTraces?: () => void;
}

const ResizablePanels: React.FC<ResizablePanelsProps> = ({
  rightPanel,
  isRightPanelOpen,
  readOnly,
  viewType,
  onClick,
  treeViewData,
  data,
  setExceptionSearchTerm,
  setViewType,
  selectedSpanId,
  narrativeTurnsData,
  narrativeTurnsLoading,
  narrativeTurnsError,
  narrativeTurnsFetchingMore,
  fetchSingleTrace,
  allTracesForFactId,
  loadingTraceId,
  setLoadingTraceId,
  errorTraceId,
  errorTraceMessage,
  factId,
  factName,
  turnChildrenData,
  turnChildrenLoading,
  spanDetails,
  onExpandTurn,
  shouldShowNarrativeView,
  tokenCount,
  tokenTooltip,
  enableTracePagination,
  tracePageSize,
  tracesTotalCount,
  tracesNextPageToken,
  tracesLoadingMore,
  onLoadMoreTraces,
}) => {
  const [leftWidth, setLeftWidth] = useState(60);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track changes to narrativeTurnsData/factId/factName to force NarrativeGraph remount
  // (but NOT on tab switches, since forceMount keeps the TabsContent alive)
  const narrativeGraphKey = useRef(0);
  const prevNarrativeTurnsData = useRef(narrativeTurnsData);
  const prevFactId = useRef(factId);
  const prevFactName = useRef(factName);

  const isDefaultNG =
    (factName === 'agent_requests' || factName === 'agent_sessions') &&
    shouldShowNarrativeView;

  // expansion-preservation logic in NarrativeGraph can run.
  const prevTurns = prevNarrativeTurnsData.current;
  const turnsIdentityChanged =
    (prevTurns == null) !== (narrativeTurnsData == null) ||
    (prevTurns != null &&
      narrativeTurnsData != null &&
      prevTurns.fact_id !== narrativeTurnsData.fact_id);

  if (
    turnsIdentityChanged ||
    prevFactId.current !== factId ||
    prevFactName.current !== factName
  ) {
    narrativeGraphKey.current += 1;
  }
  prevNarrativeTurnsData.current = narrativeTurnsData;
  prevFactId.current = factId;
  prevFactName.current = factName;

  const tabsArray = [
    {
      key: 'treeView',
      label: 'Tree View',
    },
    {
      key: 'ganttView',
      label: 'Gantt View',
    },
    ...(shouldShowNarrativeView
      ? [
          {
            key: 'narrativeView',
            label: 'Narrative View',
          },
        ]
      : []),
  ];

  const TAB_ICONS: Record<string, (className?: string) => JSX.Element> = {
    treeView: Icons.TreeViewIcon,
    ganttView: Icons.GanttViewIcon,
    narrativeView: () => Icons.CLIGraphIcon('size-4 text-[#8C9EA1]'),
  };

  const handleMouseDown = useCallback(() => {
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const newLeftWidth =
      ((e.clientX - containerRect.left) / containerRect.width) * 100;

    if (newLeftWidth >= 20 && newLeftWidth <= 80) {
      setLeftWidth(newLeftWidth);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const router = useRouter();
  const { messages } = useLanguage();

  useEffect(() => {
    if (isDefaultNG) {
      setViewType('narrativeView');
    }
  }, [factName]);

  return (
    <div
      ref={containerRef}
      className="mt-3 flex h-[720px] rounded-sm border border-[#40565A] bg-[#0000001F]"
    >
      <div
        className="flex-shrink-0"
        style={{
          width:
            isRightPanelOpen && viewType !== 'narrativeView'
              ? `${leftWidth}%`
              : '100%',
        }}
      >
        <div className="flex h-full flex-col">
          <Tabs
            value={viewType}
            onValueChange={(value) => {
              setViewType(value as 'treeView' | 'ganttView' | 'narrativeView');
            }}
            className="h-full"
          >
            <TabsList className="flex h-12 w-full justify-start rounded-none bg-[#FFFFFF0A] px-4 py-2.5">
              {tabsArray.map((tab) => {
                return (
                  <TabsTrigger
                    key={tab.key}
                    value={tab.key}
                    className={`h-7 rounded-none border border-form-control-border-rest bg-canvas-background-secondary px-2 py-[11px] text-[11px] font-normal text-white first:rounded-l last:rounded-r data-[state=active]:border-2 data-[state=active]:border-link-active data-[state=active]:bg-[#07181E] data-[state=active]:font-medium`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>{TAB_ICONS[tab.key]?.('') ?? null}</span>
                      <span>{tab.label}</span>
                    </div>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <TabsContent
              value="treeView"
              className="-mt-2 h-[calc(100%-48px)] py-2 pr-2"
            >
              <TreeView
                data={treeViewData}
                onClick={onClick}
                setExceptionSearchTerm={setExceptionSearchTerm}
                selectedSpanId={selectedSpanId}
                onTraceExpand={fetchSingleTrace}
                allTracesForFactId={allTracesForFactId}
                loadingTraceId={loadingTraceId}
                setLoadingTraceId={setLoadingTraceId}
                errorTraceId={errorTraceId}
                errorTraceMessage={errorTraceMessage}
                factId={factId}
                tokenCount={tokenCount}
                tokenTooltip={tokenTooltip}
                onWorkflowNameClick={(workflowName: string) =>
                  router.push(`/components/${workflowName}`)
                }
                enableTracePagination={enableTracePagination}
                tracePageSize={tracePageSize}
                tracesTotalCount={tracesTotalCount}
                tracesNextPageToken={tracesNextPageToken}
                tracesLoadingMore={tracesLoadingMore}
                onLoadMoreTraces={onLoadMoreTraces}
              />
            </TabsContent>
            <TabsContent
              value="ganttView"
              className="h-[calc(100%-48px)] pl-4 pr-6"
            >
              <div className="-mt-2 h-full overflow-hidden">
                {readOnly ? (
                  <GanttChart
                    data={data}
                    selectedSpanId={selectedSpanId}
                    loadingTraceId={loadingTraceId}
                    setLoadingTraceId={setLoadingTraceId}
                    errorTraceId={errorTraceId}
                    errorTraceMessage={errorTraceMessage}
                  />
                ) : (
                  <GanttChart
                    data={data}
                    onClick={onClick}
                    selectedSpanId={selectedSpanId}
                    onTraceExpand={fetchSingleTrace}
                    allTracesForFactId={allTracesForFactId}
                    loadingTraceId={loadingTraceId}
                    setLoadingTraceId={setLoadingTraceId}
                    errorTraceId={errorTraceId}
                    errorTraceMessage={errorTraceMessage}
                  />
                )}
              </div>
            </TabsContent>
            <TabsContent
              value="narrativeView"
              forceMount
              className={cn(viewType !== 'narrativeView' && 'hidden')}
            >
              <div className="-mt-2">
                {narrativeTurnsLoading ? (
                  <div className="flex h-[670px] items-center justify-center">
                    <BasicLoader />
                  </div>
                ) : narrativeTurnsError ? (
                  <div className="flex h-[670px] flex-col items-center justify-center gap-2">
                    <p className="text-red-500">
                      {messages?.Traces.error_loading_narrative_graph}
                    </p>
                    <p className="text-sm text-gray-400">
                      {narrativeTurnsError?.message || 'Unknown error'}
                    </p>
                  </div>
                ) : narrativeTurnsData && shouldShowNarrativeView ? (
                  <ErrorBoundary
                    customErrorMessage={
                      messages?.ErrorBoundary.narrative_graph_error || ''
                    }
                    className="h-[670px]"
                  >
                    <NarrativeGraph
                      key={narrativeGraphKey.current}
                      turnsData={narrativeTurnsData}
                      turnChildrenData={turnChildrenData}
                      turnChildrenLoading={turnChildrenLoading}
                      onExpandTurn={onExpandTurn}
                      iconMap={iconMap}
                      height="670px"
                      isVisible={viewType === 'narrativeView'}
                      spanDetails={spanDetails}
                      isFetchingMoreTurns={narrativeTurnsFetchingMore}
                    />
                  </ErrorBoundary>
                ) : null}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {isRightPanelOpen && viewType !== 'narrativeView' && (
        <>
          <div className="relative flex w-0 cursor-col-resize items-center justify-center">
            <div
              className="absolute top-1/2 z-10 flex h-7 w-4 items-center justify-around gap-[3px] rounded-sm border border-[#40565A] bg-[#08242F] py-1.5 pl-1 pr-[5px]"
              onMouseDown={handleMouseDown}
            >
              <hr className="h-4 w-[1px] border border-[#40565A]" />
              <hr className="h-4 w-[1px] border border-[#40565A]" />
            </div>
          </div>
          <div className="flex-1" style={{ width: `${100 - leftWidth}%` }}>
            {rightPanel}
          </div>
        </>
      )}
    </div>
  );
};

export default ResizablePanels;
